import React, { useContext, useEffect, useState } from "react";
import { Box, Flex } from "@rebass/grid";
import { SSubHeader } from "./styled/SProfile";
import { ProfileContext } from "../../providers/ProfileProvider";
import { Formik, Form } from "formik";
import { Select, Button, Header, LabeledValue } from "../../components";
import { GeoContext } from "../../providers/GeoProvider";
import * as Yup from "yup";
import { RegisterWrapper } from "../register/styled/SRegister";
import { useTranslation } from "react-i18next";

interface FormValues {
  language: string;
}

interface SelectOption {
  label: string;
  value: string;
}

const Profile = () => {
  const { profile, getProfile, updateProfile, profileLoading } = useContext(
    ProfileContext
  );
  const { languages, getLanguages } = useContext(GeoContext);
  const [profileUpdated, setUpdated] = useState(false);
  const { t, i18n } = useTranslation();
  const [currentLanguage, setCurrentLanguage] = useState({} as SelectOption);

  const ProfileSchema = Yup.object().shape({
    language: Yup.string().required(t("VALIDATION_NOTHING_CHANGED")).nullable(),
  });

  function getDefaultLang(languages: SelectOption[], profileLanguage: string) {
    const defaultLang = profileLanguage || i18n.language;
    let lang =
      languages.find((lang) => lang.value === defaultLang.toUpperCase()) ||
      ({} as SelectOption);
    if (lang.label) {
      lang.label = t(lang.label.toUpperCase());
      lang.label = upperCaseFirst(lang.label);
    }
    return lang;
  }

  function upperCaseFirst(s: string) {
    return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
  }

  useEffect(() => {
    getProfile().then((profile: any) => {
      getLanguages().then((langs) => {
        setCurrentLanguage(getDefaultLang(langs, profile["language"]));
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (setFieldValue: Function) => {
    function update(...args: any[]) {
      const selected = args[0] as SelectOption;
      setCurrentLanguage(selected);
      setFieldValue("language", selected.value);
    }
    return update;
  };

  const handleBlur = (setFieldTouched: Function) => {
    function update() {
      setFieldTouched("language", true);
    }
    return update;
  };

  return (
    <Box id="box-profile" data-page-id="profile">
      <Formik
        initialValues={{
          language: "",
        }}
        onSubmit={(values: FormValues) => {
          updateProfile({ language: values.language });
          setUpdated(true);
        }}
        validationSchema={ProfileSchema}
        render={({ touched, errors, setFieldValue, setFieldTouched }) => (
          <RegisterWrapper>
            <Header>{t("PROFILE")}</Header>
            <Form method="post">
              <Box mt={30}>
                <SSubHeader kind="primary">{t("PREFERENCES")}</SSubHeader>
              </Box>

              <Flex
                justifyContent="space-between"
                alignItems="flex-end"
                mt={30}
              >
                <Box width="100%">
                  <Select
                    required={true}
                    touched={touched.language}
                    error={errors.language}
                    label={t("LANGUAGE")}
                    name="language"
                    onChange={handleChange(setFieldValue)}
                    onBlur={handleBlur(setFieldTouched)}
                    options={languages}
                    value={currentLanguage}
                    id="languagecode"
                    placeholder={t("SELECT_PLACEHOLDER", {
                      placeholder: t("LANGUAGE"),
                    })}
                  />
                </Box>
                <Box width={80}></Box>
                <Box width="100%">
                  {profileUpdated && <p>{t("UPDATE_SUCCESSFUL")}</p>}
                </Box>
              </Flex>
              <Box mt={30}>
                <SSubHeader kind="primary">{t("PERSONAL_DETAILS")}</SSubHeader>
              </Box>

              <Flex justifyContent="space-between" mt={30}>
                <Box width="100%">
                  <LabeledValue
                    label={t("FIRST_NAME")}
                    id="firstName"
                    value={profile.firstName || "-"}
                  />
                </Box>
                <Box width={80}></Box>
                <Box width="100%">
                  <LabeledValue
                    label={t("LAST_NAME")}
                    id="lastName"
                    value={profile.lastName || "-"}
                  />
                </Box>
              </Flex>
              <Flex justifyContent="space-between" mt={30}>
                <Box width="100%">
                  <LabeledValue
                    label={t("USERNAME")}
                    id="username"
                    value={profile.username || "-"}
                  />
                </Box>
                <Box width={80}></Box>
                <Box width="100%">
                  <LabeledValue
                    label={t("PHONE_NUMBER")}
                    id="phoneNumber"
                    value={profile.phoneNumber || "-"}
                  />
                </Box>
              </Flex>
              <Flex mt={60} justifyContent="flex-end">
                <Button
                  id="button-profile-save"
                  kind="primary"
                  type="submit"
                  loading={profileLoading}
                >
                  {t("SAVE")}
                </Button>
              </Flex>
            </Form>
          </RegisterWrapper>
        )}
      />
    </Box>
  );
};

export default Profile;
